import { DateRange } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import {
  BillingMethod,
  Campaign,
  CampaignFormInputs,
  CreativeFile,
  TargetingGuidanceConfig,
  User,
  Website,
} from 'types';

export const defaultTargetingGuidanceConfig: TargetingGuidanceConfig = {
  gender: [],
  age: [],
  languages: [],
  interests: [],
  locations: [],
  industry: [],
  companySize: [],
};

export const emptyWebsite: Website = {
  url: '',
  isEditing: true,
  creatives: [],
  targetingGuidance: '',
  targetingGuidanceConfig: defaultTargetingGuidanceConfig,
};

export const defaultFormValues: CampaignFormInputs = {
  name: '',
  budget: null,
  websites: [emptyWebsite],
  campaignPeriod: [null, null],
  billingMethod: BillingMethod.ENGAGEMENT,
  maxUnitPrice: null,
  ga4PropertyId: '',

  targetingGuidance: '',
  targetingGuidanceConfig: defaultTargetingGuidanceConfig,
  targetingGuidanceMode: 'none', // "none" | "global" | "perWebsite"
  targetingGuidanceFormat: 'json', // json, text

  billingAddressEqualToContact: true,

  billingAddressSalutation: 'none',
  billingAddressFirstName: '',
  billingAddressLastName: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingAddressCountry: 'Schweiz',
  billingAddressPostcode: '',
  billingAddressCity: '',
  billingAddressEmail: '',
  billingAddressPhone: '',
  billingAddressCompanyName: '',

  contactAddressSalutation: 'none',
  contactAddressFirstName: '',
  contactAddressLastName: '',
  contactAddressLine1: '',
  contactAddressLine2: '',
  contactAddressCountry: 'Schweiz',
  contactAddressPostcode: '',
  contactAddressCity: '',
  contactAddressEmail: '',
  contactAddressPhone: '',
  contactAddressCompanyName: '',
};

const isBillingAddressDiffersFromContact = (campaign: Campaign) => {
  // contactAddress fields are the same as billingAddress
  const addressFields = Object.keys(defaultFormValues)
    .filter((key) => key.startsWith('contactAddress'))
    .map((key) => key.replace('contactAddress', ''));

  return addressFields.some((field) => {
    return (campaign as any)[`billingAddress${field}`] !== (campaign as any)[`contactAddress${field}`];
  });
};

export const getFormInputs = ({
  campaign,
  user,
}: {
  campaign: Campaign;
  user: User | undefined;
}): CampaignFormInputs => {
  const { email = '', firstName = '', lastName = '' } = user ?? {};

  const billingAddressEqualToContact = !isBillingAddressDiffersFromContact(campaign);

  const currentCampaignValues: CampaignFormInputs = {
    ...defaultFormValues,
    ...campaign,
    campaignPeriod: [
      DateTime.fromISO(String(campaign.startDate)),
      DateTime.fromISO(String(campaign.endDate)),
    ] as DateRange<DateTime>,
    websites: !campaign.websites.length
      ? defaultFormValues.websites
      : campaign.websites.map(({ id, url, targetingGuidance, targetingGuidanceConfig }) => ({
          dbId: id,
          url,
          isEditing: campaign.websites.length === 1 && url === '',
          creatives: (campaign.creatives as CreativeFile[]).filter((creative) => creative.websiteId === id),
          targetingGuidance: targetingGuidance || '',
          targetingGuidanceConfig: targetingGuidanceConfig || defaultFormValues.targetingGuidanceConfig,
        })),

    targetingGuidance: campaign.targetingGuidance,
    targetingGuidanceConfig: campaign.targetingGuidanceConfig,

    billingAddressEqualToContact,

    contactAddressEmail: campaign.contactAddressEmail ?? email,
    contactAddressFirstName: campaign.contactAddressFirstName ?? firstName,
    contactAddressLastName: campaign.contactAddressLastName ?? lastName,

    // for now we only have one predefined country
    contactAddressCountry: defaultFormValues.contactAddressCountry,
    billingAddressCountry: defaultFormValues.billingAddressCountry,
  };

  return currentCampaignValues;
};
